// router/index.tsx
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, ReactNode, Suspense } from 'react';
import { AppLayout } from '../components/Layout/AppLayout';
import { AuthGuard } from '../components/AuthGuard';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { LoadingOverlay } from '../components/LoadingOverlay';

const HomePage = lazy(() => import('../pages/Home').then((module) => ({ default: module.HomePage })));
const PostViewerPage = lazy(() => import('../pages/PostViewerPage').then((module) => ({ default: module.PostViewerPage })));
const SearchPage = lazy(() => import('../pages/Search').then((module) => ({ default: module.SearchPage })));
const AdminPage = lazy(() => import('../pages/AdminPage').then((module) => ({ default: module.AdminPage })));
const LoginPage = lazy(() => import('../pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const AboutPage = lazy(() => import('../pages/About').then((module) => ({ default: module.AboutPage })));

const Page = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<LoadingOverlay message="Loading page..." height={400} />}>
    {children}
  </Suspense>
);

// Protected layout wrapper that includes AuthGuard and AppLayout
const ProtectedLayoutWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <AuthGuard>
      <AppLayout>
        {children}
      </AppLayout>
    </AuthGuard>
  );
};

// Public layout wrapper that only includes AppLayout (no AuthGuard)
const PublicLayoutWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <AppLayout>
      {children}
    </AppLayout>
  );
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedLayoutWrapper><Page><HomePage /></Page></ProtectedLayoutWrapper>,
  },
  {
    path: '/post/:id',
    element: <ProtectedLayoutWrapper><Page><PostViewerPage /></Page></ProtectedLayoutWrapper>,
  },
  {
    path: '/search',
    element: <ProtectedLayoutWrapper><Page><SearchPage /></Page></ProtectedLayoutWrapper>,
  },
  {
    path: '/admin',
    element: (
      <ProtectedLayoutWrapper>
        <ProtectedRoute allowedRoles={['administrator']}>
          <Page><AdminPage /></Page>
        </ProtectedRoute>
      </ProtectedLayoutWrapper>
    ),
  },
  {
    path: '/about',
    element: <PublicLayoutWrapper><Page><AboutPage /></Page></PublicLayoutWrapper>,
  },
  {
    // Login page is public - no AuthGuard
    path: '/login',
    element: <PublicLayoutWrapper><Page><LoginPage /></Page></PublicLayoutWrapper>,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
